import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Play, Wrench, Target, BookOpen, PlayCircle, Trash2, Clock, RotateCcw } from 'lucide-react';import { Button } from '@/components/ui/button';
import DrillLibrary from '@/components/coach-lab/DrillLibrary';
import PracticePlanBuilder from '@/components/coach-lab/PracticePlanBuilder';
import { RunPracticeModal } from '@/components/coach-lab/RunPractice';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const NAVY = '#13314b';
const ORANGE = '#ffa133';
const MUTED = '#8FA3B1';
const BORDER = '#DDE5EC';

/* ── 2×2 brand pill ── */
function GridPill({ label, icon: Icon, bg, color, border, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="flex items-center justify-center gap-2 h-14 rounded-full text-[15px] font-semibold uppercase tracking-[.07em] transition-all hover:opacity-90 active:scale-[.97]"
      style={{
        fontFamily: "'Barlow Condensed', sans-serif",
        background: bg,
        color,
        border: border || 'none',
      }}
    >
      <Icon className="w-[16px] h-[16px] shrink-0" />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

export default function CoachLab() {
  const ctx = useOutletContext() || {};
  const { teams = [], selectedTeamId } = ctx;
  const [user, setUser] = useState(ctx.user || null);
  const [view, setView] = useState('home');           // home | build | drills
  const [plans, setPlans] = useState([]);
  const [history, setHistory] = useState([]);            // /scheduled-practices for active team
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [startingId, setStartingId] = useState(null);
  const [showRunPractice, setShowRunPractice] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // { kind, id, name }
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (ctx.user) { setUser(ctx.user); return; }
    api.get('/auth/me').then(r => setUser(r.data)).catch(() => {});
  }, [ctx.user]);

  /* ── Saved plan templates + practice history (drill plans + shot-tracker sessions) ── */
  const fetchPlans = useCallback(async () => {
    if (!selectedTeamId) { setPlans([]); setHistory([]); setLoadingPlans(false); return; }
    setLoadingPlans(true);
    try {
      const [plansRes, practicesRes, sessionsRes] = await Promise.all([
        api.get(`/practice-plans?team_id=${selectedTeamId}`),
        api.get('/scheduled-practices').catch(() => ({ data: { practices: [] } })),
        api.get('/practice/sessions?limit=50').catch(() => ({ data: [] })),
      ]);
      setPlans(plansRes.data?.plans || []);

      const practiceRows = (practicesRes.data?.practices || [])
        .filter(p => p.team_id === selectedTeamId)
        .map(p => ({
          kind: 'practice',
          id: p.practice_id,
          name: p.plan_name,
          status: p.status,
          duration_minutes: p.duration_minutes,
          drill_count: (p.sections || []).reduce((acc, s) => acc + (s.drills?.length || 0), 0),
          date: p.completed_at || p.started_at || p.created_at || null,
          raw: p,
        }));

      const sessionRows = (sessionsRes.data || []).map(s => {
        const totalMade = (s.two_pt_made || 0) + (s.three_pt_made || 0) + (s.ft_made || 0);
        const totalAtt  = (s.two_pt_attempted || 0) + (s.three_pt_attempted || 0) + (s.ft_attempted || 0);
        return {
          kind: 'shot_session',
          id: s.session_id,
          name: 'Shot Counter Session',
          status: 'completed',
          shots_made: totalMade,
          shots_attempted: totalAtt,
          date: s.updated_at || s.started_at || s.created_at || null,
          raw: s,
        };
      });

      const combined = [...practiceRows, ...sessionRows]
        .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
      setHistory(combined);
    } catch { setPlans([]); setHistory([]); }
    finally { setLoadingPlans(false); }
  }, [selectedTeamId]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  /* ── Resume an in-progress practice ── */
  const handleResumePractice = (practice) => {
    navigate(`/coach/practice/run/${practice.practice_id}`);
  };

  /* ── Re-run a completed practice (clones plan into a new in-progress run) ── */
  const handleRerunPractice = async (practice) => {
    if (!selectedTeamId) { toast.error('Select a team from the top nav first'); return; }
    setStartingId(practice.practice_id);
    try {
      const sections = practice.sections || [];
      const checklist = [];
      sections.forEach(s => {
        (s.drills || []).forEach(d => {
          checklist.push({ drill_name: d.name, section_type: s.section_type, completed: false });
        });
      });
      const team = teams.find(t => t.team_id === selectedTeamId);
      const res = await api.post('/scheduled-practices', {
        team_id: selectedTeamId,
        team_name: team?.name || practice.team_name || '',
        plan_id: practice.plan_id || null,
        plan_name: practice.plan_name,
        source: practice.source || 'saved',
        sections,
        checklist,
        duration_minutes: practice.duration_minutes || 90,
        status: 'in_progress',
        started_at: new Date().toISOString(),
      });
      navigate(`/coach/practice/run/${res.data.practice_id}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to re-run practice'));
    } finally { setStartingId(null); }
  };

  const handleDeletePractice = (practiceId, name) => {
    setPendingDelete({ kind: 'practice', id: practiceId, name: name || 'this practice' });
  };

  const handleDeleteShotSession = (sessionId) => {
    setPendingDelete({ kind: 'shot_session', id: sessionId, name: 'this tracked session' });
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const { kind, id } = pendingDelete;
    setDeleting(true);
    // Optimistic removal — drop the row from local state immediately
    setHistory(h => h.filter(r => !(r.id === id && r.kind === (kind === 'practice' ? 'practice' : 'shot_session'))));
    try {
      const url = kind === 'practice' ? `/scheduled-practices/${id}` : `/practice/sessions/${id}`;
      await api.delete(url);
      toast.success('Removed');
      setPendingDelete(null);
      // Re-sync from server to confirm purge
      fetchPlans();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to delete'));
      // Rollback — re-fetch to restore truth
      fetchPlans();
    } finally {
      setDeleting(false);
    }
  };

  /* ── Render: builder / drill library subviews ── */
  if (view === 'build') {
    return (
      <div>
        <button
          onClick={() => { setView('home'); fetchPlans(); }}
          data-testid="back-to-practice"
          className="inline-flex items-center justify-center h-8 px-3 rounded-full text-[11px] font-bold uppercase tracking-[.12em] mb-3 transition-colors"
          style={{ background: '#F1F5F9', color: '#64748B', border: '1px solid #E2E8F0', fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          ← Back to Practice
        </button>
        <PracticePlanBuilder user={user} teams={teams} selectedTeamId={selectedTeamId} />
      </div>
    );
  }
  if (view === 'drills') {
    return (
      <div>
        <button
          onClick={() => setView('home')}
          data-testid="back-to-practice"
          className="inline-flex items-center justify-center h-8 px-3 rounded-full text-[11px] font-bold uppercase tracking-[.12em] mb-3 transition-colors"
          style={{ background: '#F1F5F9', color: '#64748B', border: '1px solid #E2E8F0', fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          ← Back to Practice
        </button>
        <DrillLibrary user={user} />
      </div>
    );
  }

  /* ── Home view ── */
  return (
    <div data-testid="practice-home">
      <h1
        className="text-2xl font-extrabold uppercase tracking-tight text-slate-900 mb-5"
        style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        data-testid="coach-lab-title"
      >
        Practice
      </h1>

      {/* 2 × 2 grid */}
      <div className="grid grid-cols-2 gap-2.5 sm:gap-3 mb-7" data-testid="practice-grid">
        <GridPill
          label="Run Practice"
          icon={Play}
          bg="#198754"
          color="#fff"
          onClick={() => setShowRunPractice(true)}
          testid="grid-run-practice"
        />
        <GridPill
          label="Build Practice"
          icon={Wrench}
          bg="#13314b"
          color="#fff"
          onClick={() => setView('build')}
          testid="grid-build-practice"
        />
        <GridPill
          label="Shot Counter"
          icon={Target}
          bg={ORANGE}
          color={NAVY}
          onClick={() => navigate('/shot-counter')}
          testid="grid-shot-counter"
        />
        <GridPill
          label="Drill Library"
          icon={BookOpen}
          bg="#fff"
          color={NAVY}
          border={`1.5px solid ${BORDER}`}
          onClick={() => setView('drills')}
          testid="grid-drill-library"
        />
      </div>

      {/* Practice History */}
      <div id="saved-plans-section" data-testid="practice-history-section">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2
            className="text-[14px] font-bold uppercase tracking-[.08em]"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", color: NAVY }}
            data-testid="practice-history-header"
          >
            Practice History
          </h2>
          <button
            onClick={() => setView('build')}
            className="rounded-full inline-flex items-center justify-center gap-1.5 h-9 px-4 text-[13px] uppercase tracking-[.07em] transition-all active:scale-[.97]"
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 800,
              background: 'transparent',
              color: NAVY,
              border: `1.5px solid ${NAVY}`,
            }}
            data-testid="practice-history-new-plan"
          >
            + New Plan
          </button>
        </div>

        {loadingPlans ? (
          <p className="text-sm text-slate-400 py-6 text-center">Loading…</p>
        ) : history.length === 0 ? (
          <div
            className="rounded-xl px-4 py-8 text-center"
            style={{ background: '#fff', border: `1.5px dashed ${BORDER}` }}
            data-testid="practice-history-empty"
          >
            <p className="text-[14px] font-medium" style={{ color: NAVY }}>No practices yet</p>
            <p className="text-[12px] mt-1" style={{ color: MUTED }}>
              {plans.length === 0
                ? <>Tap <strong>Build Practice</strong> to create your first plan, then run it.</>
                : <>Run one of your saved plans from <strong>Build Practice</strong> or via Smart Practice on the Dashboard.</>}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {history.map(row => {
              const dateLabel = row.date ? new Date(row.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';

              if (row.kind === 'shot_session') {
                const pct = row.shots_attempted > 0 ? Math.round((row.shots_made / row.shots_attempted) * 100) : null;
                return (
                  <div key={`s-${row.id}`}
                    className="rounded-xl p-4 flex items-center gap-3"
                    style={{ background: '#fff', border: `1.5px solid ${BORDER}` }}
                    data-testid={`history-row-${row.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-[15px] font-bold truncate" style={{ color: NAVY, fontFamily: "'Barlow Condensed', sans-serif" }}>
                        {row.name}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-[11px]" style={{ color: MUTED }}>
                        <span className="inline-flex items-center gap-1">
                          <Target className="w-3 h-3" />
                          {row.shots_made}/{row.shots_attempted}{pct != null ? ` · ${pct}%` : ''}
                        </span>
                        {dateLabel && <span>{dateLabel}</span>}
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                          style={{ background: 'rgba(13,148,136,0.12)', color: '#0F766E', border: '1px solid rgba(13,148,136,0.35)' }}>
                          Tracked
                        </span>
                      </div>
                    </div>
                    <Button size="sm"
                      onClick={() => navigate('/shot-counter')}
                      className="rounded-full w-[112px] h-9 px-3 text-[12px] uppercase tracking-[.06em]"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, background: NAVY, color: '#fff' }}
                      data-testid={`history-track-again-${row.id}`}
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Track Again
                    </Button>
                    <button onClick={() => handleDeleteShotSession(row.id)}
                      className="w-9 h-9 flex items-center justify-center rounded-full shrink-0 transition-colors hover:bg-red-50"
                      style={{ color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA' }}
                      data-testid={`history-delete-${row.id}`}
                      aria-label="Delete from history">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              }
              // ── Practice run row ──
              const p = row.raw;
              const drillCount = row.drill_count;
              const isInProgress = row.status === 'in_progress';
              const isCompleted = row.status === 'completed';
              return (
                <div key={`p-${row.id}`}
                  className="rounded-xl p-4 flex items-center gap-3"
                  style={{ background: '#fff', border: `1.5px solid ${BORDER}` }}
                  data-testid={`history-row-${row.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-bold truncate" style={{ color: NAVY, fontFamily: "'Barlow Condensed', sans-serif" }}>
                      {row.name}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-[11px]" style={{ color: MUTED }}>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {row.duration_minutes || 90}m
                      </span>
                      <span>{drillCount} drill{drillCount === 1 ? '' : 's'}</span>
                      {dateLabel && <span>{dateLabel}</span>}
                      {isInProgress && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                          style={{ background: 'rgba(255,161,51,0.14)', color: '#B45309', border: '1px solid rgba(255,161,51,0.4)' }}
                          data-testid={`history-in-progress-${row.id}`}>
                          In progress
                        </span>
                      )}
                      {isCompleted && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                          style={{ background: 'rgba(16,185,129,0.12)', color: '#047857', border: '1px solid rgba(16,185,129,0.35)' }}>
                          Completed
                        </span>
                      )}
                    </div>
                  </div>

                  {isInProgress ? (
                    <Button size="sm"
                      onClick={() => handleResumePractice(p)}
                      className="rounded-full w-[112px] h-9 px-3 text-[12px] uppercase tracking-[.06em]"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, background: '#e8963a', color: '#fff' }}
                      data-testid={`history-resume-${row.id}`}>
                      <PlayCircle className="w-3.5 h-3.5 mr-1" /> Resume
                    </Button>
                  ) : isCompleted ? (
                    <Button size="sm"
                      onClick={() => handleRerunPractice(p)}
                      disabled={startingId === row.id}
                      className="rounded-full w-[112px] h-9 px-3 text-[12px] uppercase tracking-[.06em]"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, background: NAVY, color: '#fff' }}
                      data-testid={`history-rerun-${row.id}`}>
                      <RotateCcw className="w-3.5 h-3.5 mr-1" />
                      {startingId === row.id ? '…' : 'Re-Run'}
                    </Button>
                  ) : (
                    <Button size="sm"
                      onClick={() => handleResumePractice(p)}
                      className="rounded-full w-[112px] h-9 px-3 text-[12px] uppercase tracking-[.06em]"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, background: NAVY, color: '#fff' }}
                      data-testid={`history-start-${row.id}`}>
                      <PlayCircle className="w-3.5 h-3.5 mr-1" /> Start
                    </Button>
                  )}
                  <button onClick={() => handleDeletePractice(row.id, row.name)}
                    className="w-9 h-9 flex items-center justify-center rounded-full shrink-0 transition-colors hover:bg-red-50"
                    style={{ color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA' }}
                    data-testid={`history-delete-${row.id}`}
                    aria-label="Delete from history">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Run Practice bottom-sheet (same pattern as Smart Practice) */}
      <RunPracticeModal
        open={showRunPractice}
        onOpenChange={setShowRunPractice}
        teams={teams || []}
        selectedTeamId={selectedTeamId}
        onStart={(practiceId) => {
          setShowRunPractice(false);
          navigate(`/coach/practice/run/${practiceId}`);
        }}
      />

      {/* Delete confirmation — replaces unreliable window.confirm */}
      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => { if (!v && !deleting) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="delete-history-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete from Practice History?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove {pendingDelete?.name || 'this entry'} and purge its data. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-history-cancel" disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-history-confirm-btn"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
